/**
 * MAILBOX-UNIT account manager (2026-09-19 operatör isteği) — ONE shared card
 * adopted by all 11 mailbox-unit provider pages (rambler, aol, web.de, libero,
 * tiscali.it, tiscali.cz, vip.sina.com, sina.com, gazeta.pl, rediffmail,
 * t-online.de). Each mailbox is ONE sellable unit; there is no alias minting and
 * no renewal pool. This card gives the operator per-mailbox account tools on top
 * of the read-only site-usage view the pages already show.
 *
 * Data: the whole table + search/sort is driven by the SAME read model the page
 * uses — GET /admin/providers/:id/site-usage (mailboxes[]). Every mutation nests
 * under the existing /parents Prefix (D-225-safe). On any successful mutation the
 * card reloads its own rows AND calls the page's onChanged() so the KPI cards +
 * per-site table refresh too.
 *
 * SAFETY (non-negotiable): a mailbox that is reserved or leased is never
 * disabled/deleted/re-verified out from under a live order — the server refuses
 * with 409 and the card surfaces the Turkish reason. Default "Sil" is a
 * status-based SOFT delete that preserves every row (order forensics, site-usage
 * burns, ledger stay intact); a HARD delete is offered only for a pristine
 * mailbox, needs provider:drain, and the operator must type the accountId.
 *
 * SECRET HYGIENE: the password is never in the list, CSV, log or URL. The
 * "Şifre" column is masked by default (like the Parent/Alias pages); its
 * per-row "göster" calls the audited reveal endpoint, keeps the value only in
 * that cell's state and "gizle" drops it. The ADDRESS is not a credential and
 * is shown in full (operator request 2026-09-22).
 *
 * LOGIN FAILURES (2026-09-22): a login_failed row shows WHY (classified server
 * reason, Turkish label + tooltip) and whether the probe loop gave up (10/10).
 * "Sorunluları tekrar dene" lets the SERVER pick every retryable login_failed
 * mailbox (dry-run preview first, then one audited confirm) and grants each a
 * few fresh probe attempts; the probe loop itself does the logins.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { resolveBaseUrl, type ApiClientOptions, type ApiResult } from '../../api/client.js';
import { useApiQuery } from '../../api/hooks.js';
import { loginFailureLabel, loginFailureTitle } from '../../i18n/login-failure-reasons.js';
import { useCan } from '../../session/can.js';
import { Badge, Button, Card, CardBody, CardHeader, Icon, Input, Modal, Select, Tabs } from '../../ui/index.js';
import {
  bulkReprobe,
  bulkTakeOffSale,
  deleteMailbox,
  downloadCsv,
  exportCsv,
  fetchMailboxUnits,
  isPristineRow,
  leaseStateLabel,
  reasonText,
  reprobe,
  resetQuarantine,
  retryProblematic,
  revealPassword,
  setSaleStatus,
  statusLabel,
  toMailboxRows,
  updatePassword,
  type MailboxRow,
  type MailboxTone,
  type ReprobeResult,
  type RetryProblematicDryRun,
  type RetryProblematicResult,
  type RevealResult,
  type MailboxUnitProviderId,
  type MailboxUnitReadModel,
  type UpdatePasswordResult,
} from './mailboxUnitApi.js';

import '../../styles/mailboxunit.mobile.css';

export interface MailboxUnitManagerProps {
  readonly providerId: MailboxUnitProviderId;
  /** Operator-facing provider name, e.g. "rambler.ru". */
  readonly providerLabel: string;
  /** Called after any successful mutation so the page can refresh its KPIs. */
  readonly onChanged?: () => void;
}

type StatusFilter = 'all' | 'active' | 'login_failed' | 'suspended' | 'disabled';
type SortKey = 'address' | 'status' | 'lease' | 'lastSeen' | 'errors';

type Feedback = { readonly tone: MailboxTone; readonly text: string };

type RevealOutcome =
  | { readonly ok: true; readonly password: string }
  | { readonly ok: false; readonly reason: string };

type ModalState =
  | { readonly kind: 'password'; readonly row: MailboxRow }
  | { readonly kind: 'delete'; readonly row: MailboxRow }
  | { readonly kind: 'sale'; readonly row: MailboxRow; readonly onSale: boolean }
  | { readonly kind: 'quarantine'; readonly row: MailboxRow }
  | { readonly kind: 'bulkTakeOff' }
  | { readonly kind: 'retryProblematic'; readonly preview: RetryProblematicDryRun }
  | null;

const MIN_REASON = 4;

/**
 * Minimum probe attempts "Sorunluları tekrar dene" leaves each account (server
 * max 3). The server caps consecutive_errors at 10 - attempts, so a row that
 * already had more attempts left keeps them — "at least", not "exactly".
 */
const RETRY_ATTEMPTS = 3;

/** Mirrors the server's per-run candidate cap (RETRY_PROBLEMATIC_LIMIT). */
const RETRY_PROBLEMATIC_MAX = 500;

/** Shared operator hint: the probe loop, not the request, performs the login. */
const RESULT_ETA = 'sonuç ~1–15 dk içinde';

/** Tooltip of the "10/10 deneme" marker (repeated as a touch-only line). */
const STICKY_HINT =
  'Otomatik giriş denemesi 10 başarısız denemeden sonra durdu. Yeniden dene veya Sorunluları tekrar dene yeni deneme hakkı verir.';

/** Site badge tooltips (repeated once per card as a touch-only legend). */
const BURNED_SITE_HINT = 'satıldı — bu site için herkese kapalı';
const SERVED_SITE_HINT = 'eski satış (yanmadı) — yalnız o müşteriye kapalı';

/** Password "göster" tooltip (repeated once per card as a touch-only line). */
const REVEAL_HINT = "Şifreyi göster (audit log'a kaydedilir)";

/** Long reason labels wrap inside the cell instead of widening the table. */
const REASON_BADGE_STYLE = {
  maxWidth: '100%',
  height: 'auto',
  minHeight: 22,
  padding: '2px 8px',
  whiteSpace: 'normal',
  lineHeight: 1.35,
  textAlign: 'left',
} as const;

/**
 * Reason filter chips: .btn is nowrap + fixed height, so a long label (e.g.
 * web.de's ambiguous-refusal text) would overflow a phone-width card; let it wrap.
 */
const REASON_CHIP_STYLE = {
  maxWidth: '100%',
  height: 'auto',
  minHeight: 30,
  padding: '4px 10px',
  whiteSpace: 'normal',
  lineHeight: 1.3,
  textAlign: 'left',
} as const;

function reprobeSuccessText(data: ReprobeResult): string {
  const base = `Tek seferlik yeniden deneme sıraya alındı — ${RESULT_ETA}.`;
  return data.wasSticky === true
    ? `${base} Hesap 10/10 deneme sınırındaydı; yalnız bu tek deneme hakkı verildi.`
    : base;
}

function passwordSuccessText(data: UpdatePasswordResult): string {
  return data.reprobeQueued === true
    ? `Şifre güncellendi. Hesap yeniden kontrol için sıraya alındı — ${RESULT_ETA}.`
    : 'Şifre güncellendi.';
}

function retryAppliedText(data: RetryProblematicResult): string {
  if (data.dryRun) return 'Önizleme alındı; hiçbir hesap değiştirilmedi.';
  const parts = [`${data.nudged} hesap yeniden denemeye alındı (her birinin en az ${RETRY_ATTEMPTS} deneme hakkı var)`];
  if (data.skippedInFlight > 0) parts.push(`${data.skippedInFlight} canlı siparişte atlandı`);
  if (data.requested > data.nudged) parts.push(`${data.requested - data.nudged} hesap bu arada durum değiştirdi`);
  const capped =
    data.requested >= RETRY_PROBLEMATIC_MAX
      ? ` Tek seferde en fazla ${RETRY_PROBLEMATIC_MAX} hesap işlenir; kalanlar için işlemi tekrar çalıştırın.`
      : '';
  return `${parts.join(', ')} — ${RESULT_ETA}.${capped}`;
}

function fmt(ts: string | null | undefined): string {
  if (!ts) return '—';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('tr-TR');
}

/** Reason tooltip: hint + cleaned server reply (+ when it was last refused). */
function reasonTooltip(reason: NonNullable<MailboxRow['failureReason']>): string | undefined {
  const parts = [loginFailureTitle(reason), reason.at ? `Son ret: ${fmt(reason.at)}` : ''].filter(Boolean);
  return parts.length > 0 ? parts.join(' — ') : undefined;
}

/**
 * Presentation only: line-break opportunities (<wbr>) before the "@" and after
 * each "." of the local part, so a narrow card wraps "local" / "@domain" (or a
 * too-long local part at a dot: "aleksandr.konstantinovich." / "petrov…")
 * instead of mid-word. The "@domain" part is one unit (.mbx-addr-domain, an
 * inline-block): the browser would otherwise prefer the hyphen in
 * "@t-online.de" over the break before "@" ("…@t-" / "online.de"). The
 * browser only uses these breaks when the address does not fit, and the text
 * (textContent, copy/paste, search) is unchanged.
 */
function addressWithBreak(address: string): ReactNode {
  const at = address.indexOf('@');
  if (at <= 0) return address;
  // The local-part text nodes stay direct children of the caller's <span>;
  // the "@domain" sits in its own span (textContent is still the full address).
  const nodes: ReactNode[] = [];
  address
    .slice(0, at)
    .split('.')
    .forEach((part, i, parts) => {
      if (i < parts.length - 1) {
        nodes.push(`${part}.`, <wbr key={`d${i}`} />);
      } else {
        nodes.push(part);
      }
    });
  nodes.push(<wbr key="at" />, <span key="domain" className="mbx-addr-domain">{address.slice(at)}</span>);
  return <>{nodes}</>;
}

/**
 * Presentation only (callback ref of the row menu): a menu opened near the
 * bottom of the screen (a phone's last card) scrolls just enough to show all
 * of its items, "Sil" included. 'nearest' does nothing when it is already
 * fully visible.
 */
function revealMenu(el: HTMLDivElement | null): void {
  if (!el || typeof el.scrollIntoView !== 'function') return;
  el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
}

function rowKey(row: MailboxRow): string {
  return row.accountId || row.aliasId || row.address;
}

/** Map any non-ok ApiResult to Turkish operator text (never leaks a value). */
function apiError(res: ApiResult<unknown>): string {
  if (res.kind === 'forbidden') {
    return `Yetki yok: ${res.missingCapability ?? 'gerekli izin'} hesabınızda yok.`;
  }
  if (res.kind === 'auth_required') return reasonText('auth_required');
  if (res.kind === 'network_error') return reasonText(`network:${res.message}`);
  if (res.kind === 'error') return reasonText(res.reason);
  return 'İşlem başarısız.';
}

/**
 * Outer gate: reading the manager needs provider:inspect. Without it the card
 * does not render at all (the page's KPI read would also be denied). The
 * capability flags are resolved here and passed down so the inner component's
 * hook order never changes when the session resolves.
 */
export function MailboxUnitManager(props: MailboxUnitManagerProps): JSX.Element | null {
  const canInspect = useCan('provider:inspect');
  const canManage = useCan('provider:manage_inventory');
  const canDrain = useCan('provider:drain');
  if (!canInspect) return null;
  return <MailboxUnitManagerInner {...props} canManage={canManage} canDrain={canDrain} />;
}

function MailboxUnitManagerInner({
  providerId,
  providerLabel,
  onChanged,
  canManage,
  canDrain,
}: MailboxUnitManagerProps & { readonly canManage: boolean; readonly canDrain: boolean }): JSX.Element {
  const query = useApiQuery<MailboxUnitReadModel>((o) => fetchMailboxUnits(o, providerId), [providerId]);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  // login_failed reason chip (code); null = no reason filter.
  const [reasonFilter, setReasonFilter] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('address');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>(null);
  // Bumped per row after a successful password change so that row's
  // PasswordCell remounts masked (never keeps showing/copying the old value).
  const [credEpoch, setCredEpoch] = useState<Readonly<Record<string, number>>>({});

  const rows = useMemo<MailboxRow[]>(
    () => (query.kind === 'ok' ? toMailboxRows(query.data.mailboxes) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query.kind, query.version],
  );

  // login_failed mailboxes the server-picked bulk retry can act on (a live
  // order is never touched — the server re-checks and skips those anyway).
  const problematicCount = useMemo(
    () => rows.filter((r) => r.status === 'login_failed' && r.leaseState === 'available').length,
    [rows],
  );

  // Reason chips: login_failed rows grouped by classified reason code.
  const reasonCounts = useMemo<ReadonlyArray<{ readonly code: string; readonly count: number }>>(() => {
    const counts = new Map<string, number>();
    for (const r of rows) {
      if (r.status !== 'login_failed' || !r.failureReason) continue;
      counts.set(r.failureReason.code, (counts.get(r.failureReason.code) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([code, count]) => ({ code, count }))
      .sort((a, b) => b.count - a.count || a.code.localeCompare(b.code));
  }, [rows]);

  // A chip whose code vanished after a reload (e.g. all recovered) stops filtering.
  const activeReason = reasonFilter !== null && reasonCounts.some((c) => c.code === reasonFilter) ? reasonFilter : null;

  const visible = useMemo<MailboxRow[]>(() => {
    const q = search.trim().toLowerCase();
    const filtered = rows.filter((r) => {
      if (q && !r.address.toLowerCase().includes(q)) return false;
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (activeReason !== null && (r.status !== 'login_failed' || r.failureReason?.code !== activeReason)) {
        return false;
      }
      return true;
    });
    const sorted = [...filtered];
    sorted.sort((a, b) => {
      switch (sortKey) {
        case 'status':
          return a.status.localeCompare(b.status) || a.address.localeCompare(b.address);
        case 'lease':
          return a.leaseState.localeCompare(b.leaseState) || a.address.localeCompare(b.address);
        case 'errors':
          return b.consecutiveErrors - a.consecutiveErrors || a.address.localeCompare(b.address);
        case 'lastSeen': {
          const ta = a.lastSeenAt ? Date.parse(a.lastSeenAt) : -Infinity;
          const tb = b.lastSeenAt ? Date.parse(b.lastSeenAt) : -Infinity;
          return tb - ta || a.address.localeCompare(b.address);
        }
        case 'address':
        default:
          return a.address.localeCompare(b.address);
      }
    });
    return sorted;
  }, [rows, search, statusFilter, activeReason, sortKey]);

  const selectedIds = useMemo(
    () => visible.filter((r) => selected.has(rowKey(r)) && r.accountId).map((r) => r.accountId),
    [visible, selected],
  );

  function toggleSelect(key: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleSelectAll(): void {
    setSelected((prev) => {
      const allKeys = visible.map(rowKey);
      const allSelected = allKeys.length > 0 && allKeys.every((k) => prev.has(k));
      return allSelected ? new Set() : new Set(allKeys);
    });
  }

  function options(): ApiClientOptions {
    return { baseUrl: resolveBaseUrl() };
  }

  /** Run a mutation, refresh on success, and surface a Turkish message. */
  async function run<T>(
    key: string,
    fn: (o: ApiClientOptions) => Promise<ApiResult<T>>,
    successText: string | ((data: T) => string),
  ): Promise<boolean> {
    setBusy(key);
    setFeedback(null);
    const res = await fn(options());
    setBusy(null);
    if (res.kind === 'ok') {
      setFeedback({
        tone: 'success',
        text: typeof successText === 'function' ? successText(res.data) : successText,
      });
      query.reload();
      onChanged?.();
      return true;
    }
    setFeedback({ tone: 'danger', text: apiError(res) });
    return false;
  }

  /** Audited reveal for one row's PasswordCell; errors also surface as feedback. */
  async function doReveal(row: MailboxRow): Promise<RevealOutcome> {
    setFeedback(null);
    const res: ApiResult<RevealResult> = await revealPassword(options(), providerId, row.accountId);
    if (res.kind === 'ok') return { ok: true, password: res.data.password };
    const reason = apiError(res);
    setFeedback({ tone: 'danger', text: reason });
    return { ok: false, reason };
  }

  async function copyText(text: string): Promise<void> {
    try {
      await navigator.clipboard?.writeText(text);
      setFeedback({ tone: 'success', text: 'Panoya kopyalandı.' });
    } catch {
      setFeedback({ tone: 'warn', text: 'Kopyalanamadı — elle seçip kopyalayın.' });
    }
  }

  async function doReprobe(row: MailboxRow): Promise<void> {
    setOpenMenu(null);
    await run(`reprobe:${rowKey(row)}`, (o) => reprobe(o, providerId, row.accountId), reprobeSuccessText);
  }

  /**
   * "Sorunluları tekrar dene" step 1: server-side DRY RUN (nothing changes,
   * one audit row). The preview opens the confirm modal with the server's own
   * counts — the client never sends account ids.
   */
  async function doRetryProblematicPreview(): Promise<void> {
    setBusy('retry:preview');
    setFeedback(null);
    const res = await retryProblematic(options(), providerId, { confirm: false, attempts: RETRY_ATTEMPTS });
    setBusy(null);
    if (res.kind !== 'ok') {
      setFeedback({ tone: 'danger', text: apiError(res) });
      return;
    }
    if (!res.data.dryRun) {
      // Defensive: a non-dry-run answer to a dry-run request changed something.
      setFeedback({ tone: 'warn', text: retryAppliedText(res.data) });
      query.reload();
      onChanged?.();
      return;
    }
    setModal({ kind: 'retryProblematic', preview: res.data });
  }

  async function doBulkReprobe(): Promise<void> {
    if (selectedIds.length === 0) return;
    const ok = await run('bulk:reprobe', (o) => bulkReprobe(o, providerId, selectedIds), 'Seçilenler yeniden denemeye alındı.');
    if (ok) setSelected(new Set());
  }

  async function doBulkCsv(): Promise<void> {
    setBusy('bulk:csv');
    setFeedback(null);
    const res = await exportCsv(options(), providerId, selectedIds.length > 0 ? selectedIds : undefined);
    setBusy(null);
    if (res.kind === 'ok') {
      downloadCsv(res.data.csvBase64, `${providerId}-mailboxes-${new Date().toISOString().slice(0, 10)}.csv`);
      setFeedback({ tone: 'success', text: `CSV indirildi (${res.data.rowCount} satır, gizli sütun yok).` });
      return;
    }
    setFeedback({ tone: 'danger', text: apiError(res) });
  }

  const anySelected = selectedIds.length > 0;
  const inputsDisabled = busy !== null;

  return (
    <Card flush className="mbx-manager" data-testid="mailbox-unit-manager" data-provider={providerId} style={{ marginBottom: 16 }}>
      <CardHeader
        title="Hesap yönetimi"
        subtitle={
          <>
            {providerLabel} mailbox'larını buradan yönetin: şifre göster/değiştir, yeniden dene,
            karantinadan çıkar, satıştan kaldır/aç ve sil. Rezerve veya kirada olan bir mailbox,
            canlı sipariş kapanmadan devre dışı bırakılamaz veya silinemez.
          </>
        }
        actions={
          <div className="mbx-controls">
            <Input
              data-testid="mbx-search"
              placeholder="adres ara"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              iconLeft={<Icon.Search size={14} />}
              style={{ minWidth: 160 }}
            />
            <Select
              data-testid="mbx-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              aria-label="Duruma göre filtrele"
            >
              <option value="all">Tüm durumlar</option>
              <option value="active">Aktif</option>
              <option value="login_failed">Giriş başarısız</option>
              <option value="suspended">Askıda</option>
              <option value="disabled">Devre dışı</option>
            </Select>
            <Select
              data-testid="mbx-sort"
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
              aria-label="Sırala"
            >
              <option value="address">Adrese göre</option>
              <option value="status">Duruma göre</option>
              <option value="lease">Kiraya göre</option>
              <option value="lastSeen">Son kontrole göre</option>
              <option value="errors">Hataya göre</option>
            </Select>
            {canManage && problematicCount > 0 ? (
              <Button
                variant="primary"
                size="sm"
                data-testid="mbx-retry-problematic"
                onClick={() => void doRetryProblematicPreview()}
                loading={busy === 'retry:preview'}
                disabled={busy !== null}
                icon={<Icon.Refresh size={14} />}
                title={`Giriş başarısız hesapları hemen yeniden denemeye alır (her birine en az ${RETRY_ATTEMPTS} deneme hakkı). Önce önizleme gösterilir; canlı siparişteki hesaplara dokunulmaz.`}
              >
                Sorunluları tekrar dene ({problematicCount})
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              data-testid="mbx-refresh"
              onClick={() => query.reload()}
              icon={<Icon.Refresh size={14} />}
              aria-label="Yenile"
            >
              Yenile
            </Button>
          </div>
        }
      />
      <CardBody>
        {/* Presentation wrapper: on phones the action result and the bulk bar
            float at the bottom of the screen (styles/mailboxunit.mobile.css),
            next to the row the operator just acted on. */}
        <div className="mbx-float">
          {feedback ? (
            <div
              data-testid="mbx-feedback"
              role={feedback.tone === 'danger' ? 'alert' : 'status'}
              className={`mbx-feedback mbx-feedback-${feedback.tone}`}
            >
              {feedback.text}
            </div>
          ) : null}

          {anySelected ? (
            <div className="mbx-bulk-bar" data-testid="mbx-bulk-bar">
              <span className="mbx-bulk-count">{selectedIds.length} seçili</span>
              <Button
                variant="secondary"
                size="sm"
                data-testid="mbx-bulk-csv"
                onClick={() => void doBulkCsv()}
                loading={busy === 'bulk:csv'}
                icon={<Icon.Doc size={13} />}
                title="Yalnız gizli olmayan sütunlar — şifre asla dışa aktarılmaz"
              >
                Seçilenleri CSV indir
              </Button>
              {canManage ? (
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    data-testid="mbx-bulk-reprobe"
                    onClick={() => void doBulkReprobe()}
                    loading={busy === 'bulk:reprobe'}
                    icon={<Icon.Refresh size={13} />}
                  >
                    Seçilenleri yeniden dene
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    data-testid="mbx-bulk-takeoff"
                    onClick={() => setModal({ kind: 'bulkTakeOff' })}
                    icon={<Icon.Cancel size={13} />}
                  >
                    Seçilenleri satıştan kaldır
                  </Button>
                </>
              ) : null}
              <span className="mbx-bulk-note">gizli sütun yok</span>
            </div>
          ) : null}
        </div>

        {reasonCounts.length > 0 ? (
          <div
            data-testid="mbx-reason-chips"
            role="group"
            aria-label="Giriş hatası nedenine göre filtrele"
            style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 12 }}
          >
            <span className="t-xs t-tertiary">Giriş hatası nedeni:</span>
            <Button
              variant={activeReason === null ? 'primary' : 'secondary'}
              size="sm"
              data-testid="mbx-reason-chip-all"
              aria-pressed={activeReason === null}
              onClick={() => setReasonFilter(null)}
            >
              Tümü
            </Button>
            {reasonCounts.map(({ code, count }) => {
              const active = activeReason === code;
              const { label, hint } = loginFailureLabel(code);
              return (
                <Button
                  key={code}
                  variant={active ? 'primary' : 'secondary'}
                  size="sm"
                  data-testid={`mbx-reason-chip-${code}`}
                  aria-pressed={active}
                  title={hint || undefined}
                  style={REASON_CHIP_STYLE}
                  onClick={() => setReasonFilter(active ? null : code)}
                >
                  {label} ({count})
                </Button>
              );
            })}
            {activeReason !== null && loginFailureLabel(activeReason).hint ? (
              // Touch screens only (the chip's title needs hover): the active filter's hint.
              <span className="touch-detail mbx-chip-hint">{loginFailureLabel(activeReason).hint}</span>
            ) : null}
          </div>
        ) : null}

        {query.kind === 'idle' || query.kind === 'loading' ? (
          <div className="t-small t-tertiary" data-testid="mbx-loading">Yükleniyor…</div>
        ) : query.kind === 'auth_required' ? (
          <div role="alert" data-testid="mbx-error">{reasonText('auth_required')}</div>
        ) : query.kind === 'error' ? (
          <div role="alert" data-testid="mbx-error">
            <Badge tone="danger" style={{ marginRight: 8 }}>Hata</Badge>
            Mailbox'lar okunamadı:{' '}
            {query.reason === 'forbidden'
              ? `Yetki yok: ${query.missingCapability ?? 'provider:inspect'} izni gerekiyor.`
              : reasonText(query.reason)}
          </div>
        ) : visible.length === 0 ? (
          <div className="t-small t-tertiary" data-testid="mbx-empty">
            {rows.length === 0 ? 'Henüz mailbox yok — Import / TXT ile ekleyin.' : 'Filtreye uyan mailbox yok.'}
          </div>
        ) : (
          <>
          {/* Touch screens only (shared .touch-detail): what the red / amber
              site badges mean and that "göster" is audited live in hover
              tooltips; shown once per card, above the list. */}
          {visible.some((r) => r.burnedSites.length + r.servedSites.length > 0) ||
          (canManage && visible.some((r) => r.hasCredential)) ? (
            <div className="touch-detail mbx-touch-notes">
              {visible.some((r) => r.burnedSites.length + r.servedSites.length > 0) ? (
                <span className="mbx-sites-legend">
                  <Badge tone="danger">{BURNED_SITE_HINT}</Badge>
                  <Badge tone="warn">{SERVED_SITE_HINT}</Badge>
                </span>
              ) : null}
              {canManage && visible.some((r) => r.hasCredential) ? (
                <span className="mbx-reveal-note">
                  <Icon.Key size={12} /> {REVEAL_HINT}
                </span>
              ) : null}
            </div>
          ) : null}
          <div className="tbl-wrap" style={{ overflowX: 'auto' }}>
            <table className="tbl mbx-table" data-testid="mbx-table">
              <thead>
                <tr>
                  <th className="mbx-th-select" style={{ width: 32 }}>
                    <label className="mbx-check">
                      <input
                        type="checkbox"
                        data-testid="mbx-select-all"
                        aria-label="Tümünü seç"
                        checked={visible.length > 0 && visible.every((r) => selected.has(rowKey(r)))}
                        onChange={toggleSelectAll}
                      />
                    </label>
                  </th>
                  <th>Adres</th>
                  <th>Şifre</th>
                  <th>Durum</th>
                  <th>Oturum</th>
                  <th>Kira</th>
                  <th>Ard. hata</th>
                  <th>Son kontrol</th>
                  <th>Sunulan / Yanan siteler</th>
                  {canManage ? <th className="mbx-th-actions" style={{ width: 96 }}>İşlemler</th> : null}
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => {
                  const key = rowKey(row);
                  const st = statusLabel(row.status);
                  const lease = leaseStateLabel(row.leaseState);
                  return (
                    <tr key={key} data-testid={`mbx-row-${key}`}>
                      <td className="mbx-td-select" data-label="Seç">
                        <label className="mbx-check">
                          <input
                            type="checkbox"
                            data-testid={`mbx-select-${key}`}
                            aria-label={`${row.address} seç`}
                            checked={selected.has(key)}
                            onChange={() => toggleSelect(key)}
                          />
                        </label>
                      </td>
                      <td className="t-mono mbx-td-address" data-label="Adres">
                        <span data-testid={`mbx-address-${key}`}>{addressWithBreak(row.address)}</span>
                      </td>
                      <td className="mbx-td-password" data-label="Şifre">
                        <PasswordCell
                          key={`${key}:${credEpoch[key] ?? 0}`}
                          rowKeyValue={key}
                          canReveal={canManage && row.hasCredential}
                          onReveal={() => doReveal(row)}
                          onCopy={(text) => void copyText(text)}
                        />
                      </td>
                      <td className="mbx-td-state" data-label="Durum">
                        <span className="mbx-state" style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, maxWidth: 280 }}>
                          <Badge tone={st.tone} data-testid={`mbx-status-${key}`}>{st.text}</Badge>
                          {row.status === 'login_failed' && row.failureReason ? (
                            <Badge
                              tone="danger"
                              data-testid={`mbx-reason-${key}`}
                              title={reasonTooltip(row.failureReason)}
                              style={REASON_BADGE_STYLE}
                            >
                              {loginFailureLabel(row.failureReason.code).label}
                            </Badge>
                          ) : null}
                          {row.status === 'login_failed' && row.failureReason && reasonTooltip(row.failureReason) ? (
                            // Touch screens only: the badge's tooltip (hint, server reply, time) as text.
                            <span className="touch-detail mbx-reason-detail">{reasonTooltip(row.failureReason)}</span>
                          ) : null}
                          {!row.hasCredential ? (
                            <span className="t-xs t-tertiary">kimlik yok</span>
                          ) : null}
                          {row.sticky ? (
                            <span
                              className="t-xs"
                              style={{ color: 'var(--danger-600)' }}
                              data-testid={`mbx-sticky-${key}`}
                              title={STICKY_HINT}
                            >
                              10/10 deneme — otomatik deneme durdu
                            </span>
                          ) : null}
                          {row.sticky ? (
                            // Touch screens only: the remediation hint from the tooltip above.
                            <span className="touch-detail mbx-reason-detail">{STICKY_HINT}</span>
                          ) : null}
                        </span>
                      </td>
                      <td className="mbx-td-session" data-label="Oturum">
                        {row.status === 'login_failed' ? (
                          // session_state is meaningless while quarantined (it can still read 'active').
                          <span className="t-tertiary" data-testid={`mbx-session-${key}`}>—</span>
                        ) : (
                          <span className="t-small" data-testid={`mbx-session-${key}`}>
                            {row.verified ? 'doğrulandı' : row.sessionState}
                          </span>
                        )}
                      </td>
                      <td className="mbx-td-lease" data-label="Kira">
                        <Badge tone={lease.tone} data-testid={`mbx-lease-${key}`}>{lease.text}</Badge>
                      </td>
                      <td className="mbx-td-errors" data-label="Ard. hata">
                        {row.consecutiveErrors > 0 ? (
                          <span className="t-small">
                            {row.consecutiveErrors}
                            {row.cooldownUntil ? (
                              <span className="t-xs t-tertiary"> · tekrar {fmt(row.cooldownUntil)}</span>
                            ) : null}
                          </span>
                        ) : (
                          <span className="t-tertiary">—</span>
                        )}
                      </td>
                      <td className="t-small t-tertiary mbx-td-seen" data-label="Son kontrol">{fmt(row.lastSeenAt)}</td>
                      <td className="mbx-td-sites" data-label="Sunulan / Yanan siteler">
                        {row.burnedSites.length + row.servedSites.length === 0 ? (
                          <span className="t-tertiary">—</span>
                        ) : (
                          <span className="mbx-sites">
                            {row.burnedSites.map((s) => (
                              <Badge key={`b-${s}`} tone="danger" title={BURNED_SITE_HINT}>{s}</Badge>
                            ))}
                            {row.servedSites.map((s) => (
                              <Badge key={`s-${s}`} tone="warn" title={SERVED_SITE_HINT}>{s}</Badge>
                            ))}
                          </span>
                        )}
                      </td>
                      {canManage ? (
                        <td data-label="İşlemler" className="mbx-actions-cell mbx-td-actions">
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            data-testid={`mbx-actions-${key}`}
                            aria-haspopup="menu"
                            aria-expanded={openMenu === key}
                            onClick={() => setOpenMenu(openMenu === key ? null : key)}
                          >
                            İşlemler
                          </button>
                          {openMenu === key ? (
                            <div className="mbx-menu" role="menu" data-testid={`mbx-menu-${key}`} ref={revealMenu}>
                              <button type="button" role="menuitem" className="mbx-menu-item" data-testid={`mbx-changepw-${key}`}
                                disabled={busy !== null}
                                onClick={() => { setOpenMenu(null); setModal({ kind: 'password', row }); }}>
                                <Icon.Pencil size={13} /> Şifre değiştir
                              </button>
                              <button type="button" role="menuitem" className="mbx-menu-item" data-testid={`mbx-reprobe-${key}`}
                                disabled={busy !== null}
                                onClick={() => void doReprobe(row)}>
                                <Icon.Refresh size={13} /> Yeniden dene
                              </button>
                              {row.status === 'login_failed' ? (
                                <button type="button" role="menuitem" className="mbx-menu-item" data-testid={`mbx-resetq-${key}`}
                                  disabled={busy !== null}
                                  onClick={() => { setOpenMenu(null); setModal({ kind: 'quarantine', row }); }}>
                                  <Icon.Shield size={13} /> Karantinadan çıkar
                                </button>
                              ) : null}
                              <button type="button" role="menuitem" className="mbx-menu-item" data-testid={`mbx-sale-${key}`}
                                disabled={busy !== null}
                                onClick={() => { setOpenMenu(null); setModal({ kind: 'sale', row, onSale: row.status === 'disabled' }); }}>
                                {row.status === 'disabled' ? <Icon.Check size={13} /> : <Icon.Cancel size={13} />}
                                {row.status === 'disabled' ? ' Satışa aç' : ' Satıştan kaldır'}
                              </button>
                              <button type="button" role="menuitem" className="mbx-menu-item mbx-menu-danger" data-testid={`mbx-delete-${key}`}
                                disabled={busy !== null}
                                onClick={() => { setOpenMenu(null); setModal({ kind: 'delete', row }); }}>
                                <Icon.Trash size={13} /> Sil
                              </button>
                            </div>
                          ) : null}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </CardBody>

      {modal?.kind === 'password' ? (
        <PasswordModal
          providerId={providerId}
          row={modal.row}
          busy={inputsDisabled}
          errorText={feedback?.tone === 'danger' ? feedback.text : null}
          onClose={() => setModal(null)}
          onSubmit={async (input) => {
            const ok = await run(
              `changepw:${rowKey(modal.row)}`,
              (o) => updatePassword(o, providerId, modal.row.accountId, input),
              passwordSuccessText,
            );
            if (ok) {
              const changedKey = rowKey(modal.row);
              setCredEpoch((prev) => ({ ...prev, [changedKey]: (prev[changedKey] ?? 0) + 1 }));
              setModal(null);
            }
          }}
        />
      ) : null}

      {modal?.kind === 'delete' ? (
        <DeleteModal
          row={modal.row}
          canDrain={canDrain}
          busy={inputsDisabled}
          errorText={feedback?.tone === 'danger' ? feedback.text : null}
          onClose={() => setModal(null)}
          onSoft={async (reason) => {
            const ok = await run(
              `delete:${rowKey(modal.row)}`,
              (o) => deleteMailbox(o, providerId, modal.row.accountId, { mode: 'soft', reason }),
              'Mailbox yumuşak silindi (kayıtlar korundu, satıştan kaldırıldı).',
            );
            if (ok) setModal(null);
          }}
          onHard={async (reason, confirmationToken) => {
            const ok = await run(
              `delete:${rowKey(modal.row)}`,
              (o) => deleteMailbox(o, providerId, modal.row.accountId, { mode: 'hard', reason, confirmationToken }),
              'Mailbox kalıcı olarak silindi.',
            );
            if (ok) setModal(null);
          }}
        />
      ) : null}

      {modal?.kind === 'sale' ? (
        <ReasonModal
          testId="mbx-reason-modal"
          title={modal.onSale ? 'Satışa aç' : 'Satıştan kaldır'}
          body={
            modal.onSale
              ? <>{addressWithBreak(modal.row.address)} tekrar satışa açılacak ve yeniden doğrulanacak.</>
              : <>{addressWithBreak(modal.row.address)} satıştan kaldırılacak (devre dışı). Tüm kayıtlar korunur; istediğinizde geri açabilirsiniz.</>
          }
          confirmLabel={modal.onSale ? 'Satışa aç' : 'Satıştan kaldır'}
          danger={!modal.onSale}
          busy={inputsDisabled}
          errorText={feedback?.tone === 'danger' ? feedback.text : null}
          onClose={() => setModal(null)}
          onConfirm={async (reason) => {
            const ok = await run(
              `sale:${rowKey(modal.row)}`,
              (o) => setSaleStatus(o, providerId, modal.row.accountId, { onSale: modal.onSale, reason }),
              modal.onSale ? 'Mailbox satışa açıldı.' : 'Mailbox satıştan kaldırıldı.',
            );
            if (ok) setModal(null);
          }}
        />
      ) : null}

      {modal?.kind === 'quarantine' ? (
        <ReasonModal
          testId="mbx-reason-modal"
          title="Karantinadan çıkar"
          body={<>{addressWithBreak(modal.row.address)} tekrar aktifleştirilecek; satışa açılmadan önce kimliği yeniden doğrulanır.</>}
          confirmLabel="Karantinadan çıkar"
          busy={inputsDisabled}
          errorText={feedback?.tone === 'danger' ? feedback.text : null}
          onClose={() => setModal(null)}
          onConfirm={async (reason) => {
            const ok = await run(
              `resetq:${rowKey(modal.row)}`,
              (o) => resetQuarantine(o, providerId, modal.row.accountId, reason),
              'Karantinadan çıkarıldı — kimlik yeniden doğrulanacak.',
            );
            if (ok) setModal(null);
          }}
        />
      ) : null}

      {modal?.kind === 'bulkTakeOff' ? (
        <ReasonModal
          testId="mbx-reason-modal"
          title="Seçilenleri satıştan kaldır"
          body={`${selectedIds.length} mailbox satıştan kaldırılacak. Rezerve/kirada olanlar atlanır (canlı sipariş korunur).`}
          confirmLabel="Satıştan kaldır"
          danger
          busy={inputsDisabled}
          errorText={feedback?.tone === 'danger' ? feedback.text : null}
          onClose={() => setModal(null)}
          onConfirm={async (reason) => {
            const ok = await run(
              'bulk:takeoff',
              (o) => bulkTakeOffSale(o, providerId, selectedIds, reason),
              'Seçilenler satıştan kaldırıldı (rezerve/kirada olanlar atlandı).',
            );
            if (ok) {
              setSelected(new Set());
              setModal(null);
            }
          }}
        />
      ) : null}

      {modal?.kind === 'retryProblematic' ? (
        <RetryProblematicModal
          providerLabel={providerLabel}
          preview={modal.preview}
          busy={inputsDisabled}
          errorText={feedback?.tone === 'danger' ? feedback.text : null}
          onClose={() => setModal(null)}
          onConfirm={async () => {
            const ok = await run(
              'retry:apply',
              (o) => retryProblematic(o, providerId, { confirm: true, attempts: RETRY_ATTEMPTS }),
              retryAppliedText,
            );
            if (ok) setModal(null);
          }}
        />
      ) : null}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Modals
// ---------------------------------------------------------------------------

/**
 * A failed action's message inside the open modal. run() keeps the modal open
 * on failure and writes the message to the page feedback, which sits under the
 * dialog scrim (and under the phone sheet), so without this the confirm
 * spinner just stopped. Same text as that feedback; data-reveal lets the
 * Modal scroll it into view on a short screen.
 */
function ModalError({ text }: { readonly text: string | null }): JSX.Element | null {
  // Only a message that arrived while this modal was open: run() clears the
  // feedback when an action starts, so an older page message (from before the
  // modal opened) is never shown here.
  const [armed, setArmed] = useState(text === null);
  useEffect(() => {
    if (text === null) setArmed(true);
  }, [text]);
  if (!text || !armed) return null;
  return (
    <div role="alert" className="mbx-feedback mbx-feedback-danger mbx-modal-error" data-testid="mbx-modal-error" data-reveal="">
      {text}
    </div>
  );
}

function ReasonModal({
  testId,
  title,
  body,
  confirmLabel,
  danger = false,
  busy,
  errorText = null,
  onConfirm,
  onClose,
}: {
  readonly testId: string;
  readonly title: string;
  readonly body: ReactNode;
  readonly confirmLabel: string;
  readonly danger?: boolean;
  readonly busy: boolean;
  /** The failed action's message (same text as the page feedback, which the scrim covers). */
  readonly errorText?: string | null;
  readonly onConfirm: (reason: string) => void | Promise<void>;
  readonly onClose: () => void;
}): JSX.Element {
  const [reason, setReason] = useState('');
  const valid = reason.trim().length >= MIN_REASON;
  return (
    <Modal open onClose={onClose} title={title} testId={testId} width={440}>
      <p className="t-small" style={{ marginTop: 0 }}>{body}</p>
      <label className="field-label" htmlFor={`${testId}-reason`}>Gerekçe (en az {MIN_REASON} karakter)</label>
      <Input
        id={`${testId}-reason`}
        data-testid="mbx-reason-input"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="kısa gerekçe"
        disabled={busy}
      />
      <ModalError text={errorText} />
      <div className="dialog-footer" style={{ marginTop: 16 }}>
        <Button variant="ghost" onClick={onClose} disabled={busy}>Vazgeç</Button>
        <Button
          variant={danger ? 'danger' : 'primary'}
          data-testid="mbx-reason-confirm"
          disabled={!valid || busy}
          loading={busy}
          onClick={() => void onConfirm(reason.trim())}
        >
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}

/**
 * 2026-09-22 — "Sorunluları tekrar dene" confirm step. Every number comes from
 * the server's dry run (the server, not this page, picks the accounts); the
 * confirm re-selects on the server, so counts can shift slightly in between.
 */
function RetryProblematicModal({
  providerLabel,
  preview,
  busy,
  errorText,
  onConfirm,
  onClose,
}: {
  readonly providerLabel: string;
  readonly preview: RetryProblematicDryRun;
  readonly busy: boolean;
  readonly errorText: string | null;
  readonly onConfirm: () => void | Promise<void>;
  readonly onClose: () => void;
}): JSX.Element {
  const none = preview.candidates === 0;
  return (
    <Modal open onClose={onClose} title={`Sorunluları tekrar dene — ${providerLabel}`} testId="mbx-retry-modal" width={480}>
      {none ? (
        <p className="t-small" style={{ marginTop: 0 }} data-testid="mbx-retry-summary">
          Şu an tekrar denenebilecek hesap yok
          {preview.skippedInFlight > 0 ? ` (${preview.skippedInFlight} hesap canlı siparişte olduğu için atlandı)` : ''}.
        </p>
      ) : (
        <p className="t-small" style={{ marginTop: 0 }} data-testid="mbx-retry-summary">
          <strong>{preview.candidates} hesap</strong> ({preview.sticky} tanesi 10 deneme sınırında),{' '}
          {preview.skippedInFlight} canlı siparişte atlandı, her biri en az {RETRY_ATTEMPTS} deneme hakkıyla yeniden
          denemeye alınacak.
        </p>
      )}
      {preview.candidates >= RETRY_PROBLEMATIC_MAX ? (
        <p className="t-small" data-testid="mbx-retry-cap">
          Tek seferde en fazla {RETRY_PROBLEMATIC_MAX} hesap işlenir; kalanlar için işlemi tekrar çalıştırın.
        </p>
      ) : null}
      {preview.noCredential > 0 ? (
        <p className="t-small" data-testid="mbx-retry-nocred">
          {preview.noCredential} hesapta kayıtlı şifre yok — bunlar atlanır; önce “Şifre değiştir” ile şifre girin.
        </p>
      ) : null}
      <p className="t-xs t-tertiary" data-testid="mbx-retry-note">
        Girişi bu sayfa değil, doğrulama döngüsü yapar: {RESULT_ETA}. Proxy çalışmıyorsa denemeler proxy
        düzelince yapılır. Canlı siparişteki hesaplara dokunulmaz.
      </p>
      {errorText ? (
        <div role="alert" className="mbx-feedback mbx-feedback-danger" data-testid="mbx-retry-error" data-reveal="">
          {errorText}
        </div>
      ) : null}
      <div className="dialog-footer" style={{ marginTop: 16 }}>
        <Button variant="ghost" onClick={onClose} disabled={busy}>Vazgeç</Button>
        <Button
          variant="primary"
          data-testid="mbx-retry-confirm"
          disabled={none || busy}
          loading={busy}
          onClick={() => void onConfirm()}
        >
          {none ? 'Tekrar dene' : `${preview.candidates} hesabı tekrar dene`}
        </Button>
      </div>
    </Modal>
  );
}

/**
 * 2026-09-22 — masked-by-default password cell, same behaviour as the
 * Parent/Alias pages (ParentMailsTable PasswordCell): "göster" calls the
 * audited reveal endpoint, the value lives only in this cell's state, and
 * "gizle" re-masks by dropping it. Shown only when the caller can manage AND
 * the mailbox has a stored credential.
 */
function PasswordCell({
  rowKeyValue,
  canReveal,
  onReveal,
  onCopy,
}: {
  readonly rowKeyValue: string;
  readonly canReveal: boolean;
  readonly onReveal: () => Promise<RevealOutcome>;
  readonly onCopy: (text: string) => void;
}): JSX.Element {
  const [shown, setShown] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!canReveal) {
    return <span className="t-tertiary t-xs">—</span>;
  }

  async function onToggle(): Promise<void> {
    if (shown !== null) {
      setShown(null); // re-mask
      return;
    }
    setError(null);
    setLoading(true);
    const outcome = await onReveal();
    setLoading(false);
    if (outcome.ok) setShown(outcome.password);
    else setError(outcome.reason);
  }

  return (
    <div className="mbx-pw-cell" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span
        className="t-mono mbx-pw-value"
        style={{ fontSize: 12, userSelect: shown !== null ? 'all' : 'none', letterSpacing: shown !== null ? 0 : 1 }}
        data-testid={`mbx-password-${rowKeyValue}`}
      >
        {shown !== null ? shown : '••••••••'}
      </span>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => void onToggle()}
        loading={loading}
        title={shown !== null ? 'Şifreyi gizle' : REVEAL_HINT}
        data-testid={`mbx-password-toggle-${rowKeyValue}`}
        icon={shown !== null ? <Icon.X size={13} /> : <Icon.Key size={13} />}
      >
        {shown !== null ? 'gizle' : 'göster'}
      </Button>
      {shown !== null ? (
        <button
          type="button"
          className="iconbtn"
          data-testid={`mbx-copy-${rowKeyValue}`}
          aria-label="Şifreyi kopyala"
          onClick={() => onCopy(shown)}
        >
          <Icon.Copy size={13} />
        </button>
      ) : null}
      {error !== null ? (
        <span
          className="t-xs"
          style={{ color: 'var(--danger-600)' }}
          title={error}
          data-testid={`mbx-password-error-${rowKeyValue}`}
        >
          hata
        </span>
      ) : null}
    </div>
  );
}

function PasswordModal({
  providerId,
  row,
  busy,
  errorText = null,
  onSubmit,
  onClose,
}: {
  readonly providerId: MailboxUnitProviderId;
  readonly row: MailboxRow;
  readonly busy: boolean;
  readonly errorText?: string | null;
  readonly onSubmit: (input: { password: string; extraSecret?: string }) => void | Promise<void>;
  readonly onClose: () => void;
}): JSX.Element {
  const isAol = providerId === 'aol';
  const [password, setPassword] = useState('');
  const [webPassword, setWebPassword] = useState('');
  const [phone, setPhone] = useState('');
  const valid = password.trim().length > 0;

  function submit(): void {
    if (!valid || busy) return;
    if (isAol) {
      onSubmit({ password: password.trim(), extraSecret: `web=${webPassword};phone=${phone}` });
    } else {
      onSubmit({ password });
    }
  }

  return (
    <Modal open onClose={onClose} title={<>Şifre değiştir — {addressWithBreak(row.address)}</>} testId="mbx-password-modal" width={460}>
      {isAol ? (
        <>
          <label className="field-label" htmlFor="mbx-pw-app">Uygulama şifresi (IMAP, 16 harf)</label>
          <Input
            id="mbx-pw-app"
            data-testid="mbx-pw-app"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
          />
          <label className="field-label" htmlFor="mbx-pw-web" style={{ marginTop: 10 }}>Web şifresi</label>
          <Input
            id="mbx-pw-web"
            data-testid="mbx-pw-web"
            type="password"
            autoComplete="new-password"
            value={webPassword}
            onChange={(e) => setWebPassword(e.target.value)}
            disabled={busy}
          />
          <label className="field-label" htmlFor="mbx-pw-phone" style={{ marginTop: 10 }}>Telefon</label>
          <Input
            id="mbx-pw-phone"
            data-testid="mbx-pw-phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={busy}
          />
          <p className="t-xs t-tertiary" style={{ marginTop: 8 }}>
            IMAP girişinde yalnız uygulama şifresi kullanılır; web şifresi ve telefon ek gizli alanda saklanır.
          </p>
        </>
      ) : (
        <>
          <label className="field-label" htmlFor="mbx-pw-input">Yeni şifre</label>
          <Input
            id="mbx-pw-input"
            data-testid="mbx-pw-input"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
          />
        </>
      )}
      <p className="t-xs t-tertiary" style={{ marginTop: 8 }}>
        Şifre kimlik kasasında yeniden şifrelenir; yanıtta veya kayıtlarda asla görünmez.
      </p>
      <ModalError text={errorText} />
      <div className="dialog-footer" style={{ marginTop: 16 }}>
        <Button variant="ghost" onClick={onClose} disabled={busy}>Vazgeç</Button>
        <Button variant="primary" data-testid="mbx-pw-save" disabled={!valid || busy} loading={busy} onClick={submit}>
          Kaydet
        </Button>
      </div>
    </Modal>
  );
}

function DeleteModal({
  row,
  canDrain,
  busy,
  errorText = null,
  onSoft,
  onHard,
  onClose,
}: {
  readonly row: MailboxRow;
  readonly canDrain: boolean;
  readonly busy: boolean;
  readonly errorText?: string | null;
  readonly onSoft: (reason: string) => void | Promise<void>;
  readonly onHard: (reason: string, confirmationToken: string) => void | Promise<void>;
  readonly onClose: () => void;
}): JSX.Element {
  const hardAllowed = canDrain && isPristineRow(row) && row.accountId.length > 0;
  const [tab, setTab] = useState<'soft' | 'hard'>('soft');
  const [reason, setReason] = useState('');
  const [confirmToken, setConfirmToken] = useState('');
  const reasonValid = reason.trim().length >= MIN_REASON;
  const tokenValid = confirmToken === row.accountId;

  const tabs = hardAllowed
    ? [
        { id: 'soft', label: 'Yumuşak sil', testId: 'mbx-delete-tab-soft' },
        { id: 'hard', label: 'Kalıcı sil', testId: 'mbx-delete-tab-hard' },
      ]
    : [{ id: 'soft', label: 'Yumuşak sil', testId: 'mbx-delete-tab-soft' }];

  return (
    <Modal open onClose={onClose} title={<>Sil — {addressWithBreak(row.address)}</>} testId="mbx-delete-modal" width={480}>
      <Tabs tabs={tabs} activeId={tab} onSelect={(id) => setTab(id as 'soft' | 'hard')} testId="mbx-delete-tabs" />

      {tab === 'soft' ? (
        <div data-testid="mbx-delete-soft-pane">
          <p className="t-small" style={{ marginTop: 0 }}>
            <strong>Yumuşak sil:</strong> kayıtlar korunur, mailbox satıştan kaldırılır (devre dışı).
            Sipariş geçmişi, site kullanımı ve muhasebe bozulmaz. Rezerve/kirada bir mailbox silinemez.
          </p>
          <label className="field-label" htmlFor="mbx-delete-soft-reason">Gerekçe (en az {MIN_REASON} karakter)</label>
          <Input
            id="mbx-delete-soft-reason"
            data-testid="mbx-delete-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="kısa gerekçe"
            disabled={busy}
          />
          <ModalError text={errorText} />
          <div className="dialog-footer" style={{ marginTop: 16 }}>
            <Button variant="ghost" onClick={onClose} disabled={busy}>Vazgeç</Button>
            <Button
              variant="danger"
              data-testid="mbx-delete-soft"
              disabled={!reasonValid || busy}
              loading={busy}
              onClick={() => void onSoft(reason.trim())}
            >
              Yumuşak sil
            </Button>
          </div>
        </div>
      ) : (
        <div data-testid="mbx-delete-hard-pane">
          <p className="t-small" style={{ marginTop: 0, color: 'var(--danger-600, #dc2626)' }}>
            <strong>Kalıcı sil:</strong> geri alınamaz. Yalnız hiç satılmamış/kullanılmamış (tertemiz)
            bir mailbox için mümkündür ve sunucu bunu ayrıca doğrular. Onaylamak için hesap kimliğini yazın.
          </p>
          <label className="field-label" htmlFor="mbx-delete-hard-reason">Gerekçe (en az {MIN_REASON} karakter)</label>
          <Input
            id="mbx-delete-hard-reason"
            data-testid="mbx-delete-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="kısa gerekçe"
            disabled={busy}
          />
          <label className="field-label" htmlFor="mbx-delete-confirm-input" style={{ marginTop: 10 }}>
            Onay: hesap kimliğini yazın (<span className="t-mono">{row.accountId}</span>)
          </label>
          <Input
            id="mbx-delete-confirm-input"
            data-testid="mbx-delete-confirm-input"
            value={confirmToken}
            onChange={(e) => setConfirmToken(e.target.value)}
            placeholder={row.accountId}
            disabled={busy}
          />
          <ModalError text={errorText} />
          <div className="dialog-footer" style={{ marginTop: 16 }}>
            <Button variant="ghost" onClick={onClose} disabled={busy}>Vazgeç</Button>
            <Button
              variant="danger"
              data-testid="mbx-delete-hard"
              disabled={!reasonValid || !tokenValid || busy}
              loading={busy}
              onClick={() => void onHard(reason.trim(), confirmToken)}
            >
              Kalıcı sil
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
